import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { NavLink, Outlet } from 'react-router-dom';
import styled from 'styled-components';
import { LayoutDashboard, Box, Users, Settings, LogOut } from 'lucide-react';
import { theme } from '@styles/theme';
const Container = styled.div `
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.background.primary};
`;
const Sidebar = styled.aside `
  width: 240px;
  background-color: ${theme.colors.background.secondary};
  border-right: 1px solid ${theme.colors.border.primary};
  display: flex;
  flex-direction: column;
`;
const Logo = styled.div `
  padding: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border.primary};
`;
const LogoText = styled.h1 `
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  letter-spacing: -0.02em;
`;
const LogoTagline = styled.span `
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.muted};
  display: block;
  margin-top: 2px;
`;
const Nav = styled.nav `
  flex: 1;
  padding: ${theme.spacing.md};
`;
const NavSection = styled.div `
  margin-bottom: ${theme.spacing.lg};
`;
const NavSectionTitle = styled.h2 `
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.muted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0 ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
`;
const NavItem = styled(NavLink) `
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.secondary};
  text-decoration: none;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  transition: all ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.text.primary};
    background-color: ${theme.colors.background.tertiary};
  }

  &.active {
    color: ${theme.colors.text.primary};
    background-color: ${theme.colors.background.tertiary};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;
const SidebarFooter = styled.div `
  padding: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border.primary};
`;
const UserInfo = styled.div `
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
`;
const UserAvatar = styled.div `
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${theme.colors.background.tertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;
const UserName = styled.span `
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
`;
const LogoutButton = styled.button `
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  width: 100%;
  padding: ${theme.spacing.sm};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background: none;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.status.error};
    background-color: ${theme.colors.background.tertiary};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;
const Main = styled.main `
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
const Header = styled.header `
  height: 64px;
  padding: 0 ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${theme.colors.background.secondary};
`;
const HeaderTitle = styled.h1 `
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;
const Content = styled.div `
  flex: 1;
  padding: ${theme.spacing.lg};
  overflow-y: auto;
`;
function getInitials(name) {
    return name
        .split(' ')
        .map((part) => part[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}
export function AdminLayout({ onLogout, userName = 'Admin' }) {
    return (_jsxs(Container, { children: [_jsxs(Sidebar, { children: [_jsxs(Logo, { children: [_jsx(LogoText, { children: "Shift" }), _jsx(LogoTagline, { children: "Admin Console" })] }), _jsxs(Nav, { children: [_jsxs(NavSection, { children: [_jsx(NavSectionTitle, { children: "Overview" }), _jsxs(NavItem, { to: "/admin", end: true, children: [_jsx(LayoutDashboard, {}), "Dashboard"] })] }), _jsxs(NavSection, { children: [_jsx(NavSectionTitle, { children: "Management" }), _jsxs(NavItem, { to: "/admin/prototypes", children: [_jsx(Box, {}), "Prototypes"] }), _jsxs(NavItem, { to: "/admin/users", children: [_jsx(Users, {}), "Users"] })] }), _jsxs(NavSection, { children: [_jsx(NavSectionTitle, { children: "System" }), _jsxs(NavItem, { to: "/admin/settings", children: [_jsx(Settings, {}), "Settings"] })] })] }), _jsxs(SidebarFooter, { children: [_jsxs(UserInfo, { children: [_jsx(UserAvatar, { children: getInitials(userName) }), _jsx(UserName, { children: userName })] }), _jsxs(LogoutButton, { onClick: onLogout, children: [_jsx(LogOut, {}), "Sign Out"] })] })] }), _jsxs(Main, { children: [_jsx(Header, { children: _jsx(HeaderTitle, { children: "Admin Dashboard" }) }), _jsx(Content, { children: _jsx(Outlet, {}) })] })] }));
}
